<template>
    <div class="row g-5 g-xl-3 justify-content-between">
        <div v-if="isLoading" class="text-center">
            <span class="spinner-border spinner-border" role="status" aria-hidden="true"></span>
        </div>

        <div v-if="!isLoading" class="column col-12 col-xl-7">
            <h5 class="mb-2">{{ $t("checkout.payment_method") }}</h5>
            <p>{{ $t("checkout.confirm_continue_payment_text") }}</p>

            <payment-selector :cart="cart" @payment-method-changed="handlePaymentMethodChanged"></payment-selector>

            <hr class="my-4" />

            <div class="row g-4">
                <div class="col-12 col-md-6 d-flex flex-column align-items-start">
                    <h5 class="mb-2">{{ $t('checkout.confirm_continue_your_info') }}</h5>

                    <p>
                        {{ this.profile?.activeBillingAddress?.firstName }} {{
                            this.profile?.activeBillingAddress?.lastName
                        }}<br />
                        {{ this.profile?.activeBillingAddress?.street }}<br />
                        {{ this.profile?.activeBillingAddress?.zipcode }} {{ this.profile?.activeBillingAddress?.city
                        }}<br />
                        {{ country?.translated?.name }}
                    </p>

                    <p>{{ email }}</p>

                    <a href="#" class="icon-link mt-auto" data-bs-toggle="modal" data-bs-target="#modalEditAddressInfo">
                        <PhPencilSimple weight="bold" />
                        <span>{{ $t('checkout.btn_edit_info') }}</span>
                    </a>
                </div>

                <div class="col-12 col-md-6 d-flex flex-column align-items-start">
                    <h5 class="mb-2">{{ $t('checkout.confirm_continue_delivery') }}</h5>

                    <template v-if="deliveryMethod === 'DEALER'">
                        <p class="small mb-1">{{ $t('checkout.confirm_continue_free_pickup_text') }}</p>

                        <p class="h5 text-dark">{{ cart?.extensions?.dealer?.translated.name }}</p>
                        <p>{{ cart?.extensions.dealer.address }}, {{ cart?.extensions.dealer.zipcode }} {{
                                cart?.extensions.dealer.city }}</p>

                        <a :href="dealerSelectRoute" class="icon-link mt-auto">
                            <PhPencilSimple weight="bold" />
                            <span>{{ $t('checkout.btn_edit_dealer') }}</span>
                        </a>
                    </template>
                    <template v-if="deliveryMethod === 'DELIVERY'">
                        <p class="small"
                           v-if="this.profile?.activeShippingAddress?.id === this.profile?.activeBillingAddress?.id">
                            {{ $t('checkout.confirm_continue_home_delivery_text') }}
                        </p>

                        <template
                            v-if="this.profile?.activeShippingAddress?.id !== this.profile?.activeBillingAddress?.id">
                            <p class="small mb-1">{{
                                    $t('checkout.confirm_continue_home_delivery_different_address_text') }}</p>

                            <p>
                                {{ this.profile?.activeShippingAddress?.firstName }} {{
                                    this.profile?.activeShippingAddress?.lastName }}<br />
                                {{ this.profile?.activeShippingAddress?.street }}<br />
                                {{ this.profile?.activeShippingAddress?.zipcode }} {{
                                    this.profile?.activeShippingAddress?.city
                                }}<br />
                                {{ deliveryCountry?.translated?.name }}
                            </p>
                        </template>

                        <a href="#" class="icon-link mt-auto" data-bs-toggle="modal" data-bs-target="#modalEditShippingInfo">
                            <PhPencilSimple weight="bold" />
                            <span>{{ $t('checkout.btn_edit_delivery') }}</span>
                        </a>
                    </template>
                </div>
            </div>

            <hr class="my-4" />

            <h5 class="mb-2">
                {{ $t("checkout.confirm_continue_products_in_order") }}
            </h5>

            <cart-line-items :cart="cart" :is-lifestyle="isLifestyle" @update-cart="updateCart"></cart-line-items>
        </div>

        <div v-if="!isLoading" class="column col-12 col-xl-4">
            <div class="sticky-top">
                <cart-summary :cart="cart" :redirect="failureRedirect" />

                <button
                    v-on:click="proceedToPayment"
                    class="btn btn-secondary w-100 mt-1 position-relative"
                    :disabled="isCreatingOrder || !selectedPaymentMethod"
                >
                    <div class="position-absolute top-50 start-50 translate-middle" :class="{ 'd-none': !isCreatingOrder }">
                        <span class="spinner-border spinner-border-sm text-light" role="status" aria-hidden="true"></span>
                    </div>

                    <div class="d-flex align-items-center justify-content-center" :class="{ 'opacity-0': isCreatingOrder }">
                        <p class="mb-0">
                            {{ $t("checkout.btn_confirm_and_pay") }}
                        </p>
                    </div>
                </button>

                <div v-if="!selectedPaymentMethod" class="alert alert-warning mt-2 small text-center">
                    {{ $t("checkout.please_select_payment_method") }}
                </div>
            </div>
        </div>

        <!-- MODALS -->

        <!-- modalEditAddressInfo -->
        <div class="modal fade" id="modalEditAddressInfo" tabindex="-1" aria-labelledby="modalEditAddressInfoLabel" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h3 class="modal-title" id="modalEditAddressInfoLabel">{{ $t('account.address_info_edit') }}</h3>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div class="form-style form-light custom">
                            <form @submit.prevent="updateBillingAddressInfo" id="addressInfoForm">
                                <CustomerAddress
                                    :firstName.sync="firstName"
                                    :lastName.sync="lastName"
                                    :street.sync="street"
                                    :zipcode.sync="zipcode"
                                    :city.sync="city"
                                    :countryId.sync="countryId"
                                    :countries="countries"
                                    :phoneNumber.sync="phoneNumber"
                                    :showNameFields="true"
                                />
                            </form>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-light" data-bs-dismiss="modal">
                            {{ $t('account.btn_cancel') }}
                        </button>

                        <button type="submit" form="addressInfoForm" class="btn btn-primary position-relative">
                            <div class="position-absolute top-50 start-50 translate-middle" :class="{ 'd-none': !isUpdatingAddressInfo }">
                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            </div>

                            <div class="d-flex align-items-center" :class="{ 'opacity-0': isUpdatingAddressInfo }">
                                <p class="mb-0">{{ $t('account.btn_edit') }}</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- END modalEditAddressInfo -->

        <!-- modalEditShippingInfo -->
        <div class="modal fade" id="modalEditShippingInfo" tabindex="-1" aria-labelledby="modalEditShippingInfoLabel" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h3 class="modal-title" id="modalEditShippingInfoLabel">{{ $t('account.address_info_edit') }}</h3>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div class="form-style form-light custom">
                            <form @submit.prevent="updateShippingAddressInfo" id="addressShippingInfoForm">
                                <CustomerAddress
                                    :firstName.sync="deliveryFirstName"
                                    :lastName.sync="deliveryLastName"
                                    :street.sync="deliveryStreet"
                                    :zipcode.sync="deliveryZipcode"
                                    :city.sync="deliveryCity"
                                    :countryId.sync="deliveryCountryId"
                                    :countries="countries"
                                    :phoneNumber.sync="deliveryPhoneNumber"
                                    :showNameFields="true"
                                />
                            </form>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-light" data-bs-dismiss="modal">
                            {{ $t('account.btn_cancel') }}
                        </button>

                        <button type="submit" form="addressShippingInfoForm" class="btn btn-primary position-relative">
                            <div class="position-absolute top-50 start-50 translate-middle" :class="{ 'd-none': !isUpdatingAddressInfo }">
                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            </div>

                            <div class="d-flex align-items-center" :class="{ 'opacity-0': isUpdatingAddressInfo }">
                                <p class="mb-0">{{ $t('account.btn_edit') }}</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- END modalEditShippingInfo -->

        <!-- Payment session expired modal -->
        <div class="modal fade" id="modalPaymentExpired" tabindex="-1" aria-labelledby="modalPaymentExpiredLabel" aria-hidden="true" data-bs-backdrop="static">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h3 class="modal-title" id="modalPaymentExpiredLabel">{{ $t('checkout.payment_session_expired_title') }}</h3>
                    </div>
                    <div class="modal-body">
                        <p>{{ $t('checkout.payment_session_expired_message') }}</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-primary" @click="refreshCheckout">
                            {{ $t('checkout.payment_session_refresh') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- END Payment session expired modal -->
    </div>
</template>

<script>
import CartLineItems from './CartLineItems.vue';
import ShipmentSelector from './ShipmentSelector.vue';
import CartSummary from './CartSummary.vue';
import PaymentSelector from './PaymentSelector.vue';

import shopware from '../../mixins/shopware';
import customer from '../../mixins/customer';

import { EventBus } from '../../api/eventBus.ts';
import { PhPencilSimple } from 'phosphor-vue';
import { Modal } from 'bootstrap';
import lineItem from "../../mixins/line-item";

export default {
    components: {
        CartLineItems,
        ShipmentSelector,
        CartSummary,
        PaymentSelector,
        PhPencilSimple,
    },
    mixins: [shopware, customer],
    props: {
        dealerSelectRoute: String,
        failureRedirect: String,
        paymentFailureRedirect: String,
        paymentSuccessRedirect: String,
        prepaidPaymentMethodId: String,
        isLifestyle: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            cart: null,
            order: null,
            error: null,
            profile: {},
            isLoading: false,
            isLoggedIn: false,

            // modals
            addressInfoModal: null,
            shippingInfoModal: null,
            paymentExpiredModal: null,

            // loaders
            isUpdatingPersonalInfo: false,
            isUpdatingAddressInfo: false,
            isCreatingOrder: false,

            // data
            salutationId: null,
            salutation: null,
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: '',
            street: '',
            zipcode: '',
            city: '',
            countryId: null,
            country: '',
            deliveryFirstName: '',
            deliveryLastName: '',
            deliveryPhoneNumber: '',
            deliveryStreet: '',
            deliveryZipcode: '',
            deliveryCity: '',
            deliveryCountryId: null,
            deliveryCountry: '',

            // payment tracking
            selectedPaymentMethod: null,
            paymentAttempts: 0,
            maxPaymentAttempts: 3,
            paymentTimeoutId: null
        };
    },
    created: function () {
        this.init();

        // Listen for payment method changes from PaymentSelector
        EventBus.$on('payment-method-changed', (methodId) => {
            this.selectedPaymentMethod = methodId;
        });

        // Mirror Cart.vue: managed CartSummary on this page reads from our `cart` data prop, so any cart-updated
        // event from a sibling (ShipmentSelector enrichment, ShipmentSelector updateShippingMethod, voucher,
        // restore-from-order) MUST flow into our `cart` here or the summary will go stale. CartLineItems'
        // @update-cart Vue $emit is already wired to updateCart(); this listener handles the EventBus path.
        EventBus.$on('cart-updated', this.handleCartUpdated);
    },
    mounted: function () {
        this.addressInfoModal = new Modal(document.getElementById('modalEditAddressInfo'));
        this.shippingInfoModal = new Modal(document.getElementById('modalEditShippingInfo'));
        this.paymentExpiredModal = new Modal(document.getElementById('modalPaymentExpired'));

        // Check for payment return URL parameters
        this.checkPaymentReturnStatus();
    },
    beforeDestroy() {
        EventBus.$off('payment-method-changed');
        EventBus.$off('cart-updated', this.handleCartUpdated);
        this.clearPaymentTimeout();
    },
    computed: {
        deliveryMethod() {
            if (this.cart && this.cart?.extensions?.dealer && this.cart?.deliveries[0]?.shippingMethod?.technicalName === 'shipping_dealer') {
                return 'DEALER';
            } else {
                return 'DELIVERY';
            }
        }
    },
    methods: {
        async init() {
            this.isLoading = true;

            try {
                await this.getContext();

                const promises = [
                    this.getCart(),
                    this.getCountries(),
                ];

                await Promise.all(promises);
                this.initProfile();
                this.$emit('cart-updated', this.cart);

                // Set selected payment method from current context
                if (this.context && this.context.paymentMethod && this.context.paymentMethod.id) {
                    this.selectedPaymentMethod = this.context.paymentMethod.id;
                }
            } catch (error) {
                console.error("Error initializing checkout:", error);
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.init_error'),
                    state: 'error'
                });
            } finally {
                this.isLoading = false;
            }
        },

        async initProfile() {
            this.profile = this.context.customer;

            if (this.profile) {
                this.salutationId = this.profile.salutationId;
                this.firstName = this.profile?.firstName;
                this.lastName = this.profile?.lastName;
                this.email = this.profile.email;

                if (this.profile.activeBillingAddress && this.profile.activeBillingAddress.id) {
                    this.lastName = this.profile?.activeBillingAddress?.lastName;
                    this.firstName = this.profile?.activeBillingAddress?.firstName;
                    this.street = this.profile?.activeBillingAddress?.street;
                    this.addressNumber = this.profile?.activeBillingAddress?.addressNumber;
                    this.addressSupplement = this.profile?.activeBillingAddress?.addressSupplement;
                    this.zipcode = this.profile?.activeBillingAddress?.zipcode;
                    this.city = this.profile?.activeBillingAddress?.city;
                    this.countryId = this.profile?.activeBillingAddress?.countryId;
                    this.country = this.profile?.activeBillingAddress?.country;
                    this.phoneNumber = this.profile?.activeBillingAddress?.phoneNumber;
                }

                if (this.profile.activeShippingAddress && this.profile.activeShippingAddress.id) {
                    this.deliveryDifferentAsBilling = true;
                    this.deliveryFirstName = this.profile?.activeShippingAddress?.firstName;
                    this.deliveryLastName = this.profile?.activeShippingAddress?.lastName;
                    this.deliveryStreet = this.profile?.activeShippingAddress?.street;
                    this.deliveryAddressNumber = this.profile?.activeShippingAddress?.addressNumber;
                    this.deliveryAddressSupplement = this.profile?.activeShippingAddress?.addressSupplement;
                    this.deliveryZipcode = this.profile?.activeShippingAddress?.zipcode;
                    this.deliveryCity = this.profile?.activeShippingAddress?.city;
                    this.deliveryCountryId = this.profile?.activeShippingAddress?.countryId;
                    this.deliveryCountry = this.profile?.activeShippingAddress?.country;
                    this.deliveryPhoneNumber = this.profile?.activeShippingAddress?.phoneNumber;
                }

                this.isLoggedIn = true;
            } else {
                this.isLoggedIn = false;
            }
        },

        handlePaymentMethodChanged(methodId) {
            this.selectedPaymentMethod = methodId;
        },

        checkPaymentReturnStatus() {
            // Check URL parameters for payment status
            const urlParams = new URLSearchParams(window.location.search);
            const paymentStatus = urlParams.get('paymentStatus');

            if (paymentStatus === 'failed' || paymentStatus === 'cancelled') {
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.payment_failed'),
                    state: 'error'
                });
                // Clear the URL parameters without page refresh
                const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
                window.history.replaceState({path: newUrl}, '', newUrl);
            } else if (paymentStatus === 'expired') {
                this.paymentExpiredModal.show();
                // Clear the URL parameters without page refresh
                const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
                window.history.replaceState({path: newUrl}, '', newUrl);
            }
        },

        refreshCheckout() {
            this.paymentExpiredModal.hide();
            this.init();
        },

        async getCart() {
            try {
                if (localStorage.getItem("lastOrderId") !== null) {
                    await this.restoreCart();
                } else {
                    this.cart = await this.api.client.invoke("readCart get /checkout/cart");
                    localStorage.setItem("transactionId", this.cart.token);
                }
                return this.cart;
            } catch (error) {
                console.error("Error fetching cart:", error);
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.cart_load_error'),
                    state: 'error'
                });
                throw error;
            }
        },

        async restoreCart() {
            this.error = null;

            try {
                // Get the last order ID from local storage
                const failedOrderId = localStorage.getItem("lastOrderId");

                if (!failedOrderId) {
                    throw new Error("No failed order ID found");
                }

                // Fetch the failed order
                const response = await this.api.client.invoke("readOrder post /order", {
                    "limit": 1,
                    "ids": [failedOrderId],
                    "associations": {
                        "lineItems": {},
                        "deliveries": {
                            "associations": {
                                "shippingMethod": {}
                            }
                        },
                        "transactions": {
                            "associations": {
                                "paymentMethod": {}
                            }
                        }
                    }
                });

                if (!response.orders || !response.orders.elements || response.orders.elements.length === 0) {
                    throw new Error("Failed order not found");
                }

                const failedOrder = response.orders.elements[0];

                // Create or get cart
                this.cart = await this.api.client.invoke("readCart get /checkout/cart");
                localStorage.setItem("transactionId", this.cart.token);

                // Add items from the failed order to the new cart
                const lineItemPromises = failedOrder.lineItems
                    .filter(lineItem => lineItem.payload.acrisProductVoucherCodes)
                    .map(lineItem =>
                    this.api.client.invoke("addLineItem post /checkout/cart/line-item", {
                        items: [{
                            id: lineItem.id,
                            modified: lineItem.modified,
                            type: lineItem.type,
                            referencedId: lineItem.referencedId,
                            quantity: lineItem.quantity,
                            good: lineItem.good,
                            payload: {
                                productUrl: lineItem.payload?.productUrl,
                                description: lineItem.payload?.description,
                                variant: lineItem.payload?.variant,
                                coverImg: lineItem.payload?.coverImg
                            }
                        }]
                    })
                );

                await Promise.all(lineItemPromises);

                // Restore shipping method if available
                if (failedOrder.deliveries &&
                    failedOrder.deliveries.length > 0 &&
                    failedOrder.deliveries[0].shippingMethod) {
                    await this.api.client.invoke("updateContext patch /context", {
                        shippingMethodId: failedOrder.deliveries[0].shippingMethod.id
                    });
                }

                // Restore payment method if available
                if (failedOrder.transactions &&
                    failedOrder.transactions.length > 0 &&
                    failedOrder.transactions[0].paymentMethod) {
                    this.selectedPaymentMethod = failedOrder.transactions[0].paymentMethod.id;
                    await this.api.client.invoke("updateContext patch /context", {
                        paymentMethodId: failedOrder.transactions[0].paymentMethod.id
                    });
                }

                // Restore dealer if available
                if (failedOrder.customFields && failedOrder.customFields.dealer) {
                    await this.api.client.invoke(`dealerToCart patch /checkout/cart/dealer`, {
                        token: this.context.token,
                        customer: this.context.customer.id,
                        dealer: failedOrder.customFields.dealer
                    });
                }

                // Get the updated cart
                this.cart = await this.api.client.invoke("readCart get /checkout/cart");

                // Notify the user about the restored cart
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.cart_restored'),
                    state: 'success'
                });

                localStorage.removeItem("lastOrderId");
            } catch (err) {
                this.error = `Failed to restore cart: ${err.message}`;
                console.error(err);

                if (err.status && (err.status === 403 || err.status === 404)) {
                    return;
                }

                // If restoration fails, create a new cart
                this.cart = await this.api.client.invoke("readCart get /checkout/cart");
                localStorage.setItem("transactionId", this.cart.token);
                localStorage.removeItem("lastOrderId");

                EventBus.$emit("trigger-alert", {
                    message: this.$t('checkout.cart_restore_error'),
                    state: "warning",
                });
            }
        },

        updateCart(updatedCart) {
            this.cart = updatedCart;
            localStorage.setItem("transactionId", this.cart.token);
        },

        // EventBus 'cart-updated' handler. Distinct from updateCart() which is bound to CartLineItems'
        // @update-cart Vue $emit. ShipmentSelector and other siblings emit through EventBus only — without
        // this listener their cart mutations would not propagate into this component's `cart` data prop,
        // and the managed CartSummary (which now reads only from prop) would render stale values.
        handleCartUpdated(updatedCart) {
            if (!updatedCart || !Array.isArray(updatedCart.lineItems)) return;
            this.cart = updatedCart;
            if (updatedCart.token) {
                localStorage.setItem("transactionId", updatedCart.token);
            }
        },

        async updateBillingAddressInfo() {
            // Check if anything has changed
            if (this.firstName === this.profile?.activeBillingAddress?.firstName &&
                this.lastName === this.profile?.activeBillingAddress?.lastName &&
                this.street === this.profile?.activeBillingAddress?.street &&
                this.zipcode === this.profile?.activeBillingAddress?.zipcode &&
                this.city === this.profile?.activeBillingAddress?.city &&
                this.countryId === this.profile?.activeBillingAddress?.countryId &&
                this.phoneNumber === this.profile?.activeBillingAddress?.phoneNumber
            ) {
                this.addressInfoModal.hide();
                return;
            }

            const data = {
                street: this.street,
                zipcode: this.zipcode,
                city: this.city,
                countryId: this.countryId,
                firstName: this.firstName,
                lastName: this.lastName,
                phoneNumber: this.phoneNumber
            };

            await this.changeAddress(null, this.profile?.activeBillingAddress?.id, data);

            this.addressInfoModal.hide();
        },

        async updateShippingAddressInfo() {
            // Check if anything has changed
            if (this.deliveryFirstName === this.profile.activeShippingAddress.firstName &&
                this.deliveryLastName === this.profile.activeShippingAddress.lastName &&
                this.deliveryStreet === this.profile.activeShippingAddress.street &&
                this.deliveryZipcode === this.profile.activeShippingAddress.zipcode &&
                this.deliveryCity === this.profile.activeShippingAddress.city &&
                this.deliveryCountryId === this.profile.activeShippingAddress.countryId &&
                this.deliveryPhoneNumber === this.profile.activeShippingAddress.phoneNumber
            ) {
                this.shippingInfoModal.hide();
                return;
            }

            const data = {
                street: this.deliveryStreet,
                zipcode: this.deliveryZipcode,
                city: this.deliveryCity,
                countryId: this.deliveryCountryId,
                firstName: this.deliveryFirstName,
                lastName: this.deliveryLastName,
                phoneNumber: this.deliveryPhoneNumber,
            };

            const shippingAddressId = this.findShippingSpecificAddressId(this.profile);

            if (shippingAddressId) {
                // update existing address
                await this.changeAddress(null, shippingAddressId, data, 'shipping');
            } else {
                // create new address + set as default
                await this.changeAddress(this.profile.id, null, data, 'shipping');
            }

            this.shippingInfoModal.hide();
        },

        async changeAddress(customerId = null, addressId = null, data, setDefault = null) {
            if (!customerId && !addressId) {
                return;
            }

            try {
                this.isUpdatingAddressInfo = true;

                if (addressId) {
                    await this.updateCustomerAddress(addressId, data, setDefault);
                }

                if (customerId) {
                    await this.createCustomerAddress(customerId, data, setDefault);
                }

                await this.getContext();
                this.initProfile();

                EventBus.$emit('trigger-alert', {
                    message: this.$t('messages.changed_address'),
                    state: 'success'
                });
            } catch (error) {
                console.error("Error updating address:", error);
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.address_update_error'),
                    state: 'error'
                });
            } finally {
                this.isUpdatingAddressInfo = false;
            }
        },

        getGTMItems() {
            if (!this.cart || !this.cart.lineItems) return [];

            return this.cart.lineItems.map((item, index) => ({
                item_id: item.payload?.productNumber || '',
                item_name: item.label,
                item_list_name: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher'),
                item_brand: item.payload?.manufacturer?.name || "DCM",
                price: item.price?.unitPrice || 0,
                quantity: item.quantity || 1,
                index: index + 1,
                currency: 'EUR',
                item_category: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher')
            }));
        },

        async resolveOrderAndHandlePayment() {
            // Setup a payment timeout for better user experience
            this.setupPaymentTimeout();

            try {
                // Step 1: Create order from cart if not exists
                if (!localStorage.getItem('lastOrderId')) {
                    this.order = await this.api.client.invoke("createOrder post /checkout/order");

                    // Step 2: Store the order ID in localStorage
                    localStorage.setItem("lastOrderId", this.order.id);
                } else {
                    // Get the existing order
                    const response = await this.api.client.invoke("readOrder post /order", {
                        "limit": 1,
                        "ids": [localStorage.getItem('lastOrderId')]
                    });

                    if (response.orders && response.orders.elements && response.orders.elements.length > 0) {
                        this.order = response.orders.elements[0];
                    } else {
                        // If order doesn't exist, create a new one
                        this.order = await this.api.client.invoke("createOrder post /checkout/order");
                        localStorage.setItem("lastOrderId", this.order.id);
                    }
                }

                // Step 3: Handle Payment
                return await this.api.client.invoke(
                    "handlePayment post /handle-payment",
                    {
                        orderId: localStorage.getItem('lastOrderId'),
                        finishUrl: this.paymentSuccessRedirect,
                        errorUrl: `${this.paymentFailureRedirect}?paymentStatus=failed`,
                    }
                );
            } catch (error) {
                console.error("Error in resolveOrderAndHandlePayment:", error);
                throw error;
            }
        },

        setupPaymentTimeout() {
            // Clear any existing timeout
            this.clearPaymentTimeout();

            // Set a new timeout (5 minutes is a common payment session timeout)
            this.paymentTimeoutId = setTimeout(() => {
                // If we're still on the page after timeout, show expired notification
                if (this.isCreatingOrder) {
                    this.isCreatingOrder = false;
                    this.paymentExpiredModal.show();
                    EventBus.$emit("payment-session-expired");
                }
            }, 5 * 60 * 1000); // 5 minutes
        },

        clearPaymentTimeout() {
            if (this.paymentTimeoutId) {
                clearTimeout(this.paymentTimeoutId);
                this.paymentTimeoutId = null;
            }
        },

        async proceedToPayment() {
            if (this.isCreatingOrder || !this.selectedPaymentMethod) {
                return;
            }

            try {
                this.isCreatingOrder = true;
                this.paymentAttempts++;

                // Verify cart is valid
                if (!this.cart || !this.cart.lineItems || this.cart.lineItems.length === 0) {
                    EventBus.$emit('trigger-alert', {
                        message: this.$t('checkout.empty_cart'),
                        state: 'warning'
                    });
                    this.isCreatingOrder = false;
                    return;
                }

                // Track the payment info event for analytics
                const paymentMethods = document.getElementsByName('paymentMethod');
                let selectedPaymentMethod = "";
                if (paymentMethods.length > 0) {
                    for (let i = 0; i < paymentMethods.length; i++) {
                        if (paymentMethods[i].checked) {
                            selectedPaymentMethod = paymentMethods[i].dataset.valuetext;
                            break;
                        }
                    }

                    if (this.cart && this.cart.lineItems.length > 0) {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                            'event': 'add_payment_info',
                            'payment_type': selectedPaymentMethod,
                            'transaction_id': localStorage.getItem('transactionId') ?? this.cart.token,
                            'tax': this.cart.price.totalPrice - this.cart.price.netPrice,
                            'value': this.cart.price.totalPrice,
                            'items': this.getGTMItems()
                        });
                    }
                }

                // Process payment
                let payment = await this.resolveOrderAndHandlePayment();

                // Clear payment timeout
                this.clearPaymentTimeout();

                if (!payment || payment.redirectUrl === null) {
                    // Zero-total orders (e.g. fully covered by voucher) or prepaid orders
                    // have no payment redirect — treat as successful
                    if (this.selectedPaymentMethod === this.prepaidPaymentMethodId || this.cart?.price?.totalPrice === 0) {
                        window.location.href = this.paymentSuccessRedirect;
                        return;
                    }

                    // If payment failed with no redirect, go to failure page
                    window.location.href = `${this.paymentFailureRedirect}?paymentStatus=failed`;
                    return;
                }

                // Reset payment method to default once completed (optional)
                await this.api.client.invoke("updateContext patch /context", {
                    paymentMethodId: this.context.salesChannel.paymentMethodId
                });

                this.isCreatingOrder = false;

                // Redirect to payment provider
                window.location.href = payment.redirectUrl || this.paymentSuccessRedirect;
            } catch (error) {
                console.error("Error during payment:", error);

                // Handle retry logic
                if (this.paymentAttempts < this.maxPaymentAttempts) {
                    EventBus.$emit("trigger-alert", {
                        message: this.$t("checkout.payment_retry"),
                        state: "warning",
                    });
                    // Wait a moment before retrying
                    setTimeout(() => {
                        this.isCreatingOrder = false;
                        this.proceedToPayment();
                    }, 1000);
                } else {
                    // If max retries reached, show error and redirect
                    EventBus.$emit("trigger-alert", {
                        message: this.$t("checkout.payment_failed"),
                        state: "error",
                    });
                    setTimeout(() => {
                        window.location.href = `${this.failureRedirect}?paymentStatus=failed`;
                    }, 2000);
                }
            } finally {
                if (this.paymentAttempts >= this.maxPaymentAttempts) {
                    this.isCreatingOrder = false;
                    this.paymentAttempts = 0;
                }
            }
        }
    }
};
</script>
